import * as Sentry from '@sentry/react'
import LogRocket from 'logrocket'
import LogRocketFuzzySanitizer from 'logrocket-fuzzy-search-sanitizer'
import { PropsWithChildren, useEffect } from 'react'

import { isNotNullOrUndefined } from '@common/util/isNotNullOrUndefined'
import { useAdminContext } from '~/components/hooks/useAdminContext'
import { useCurrentUser } from '~/components/hooks/useCurrentUser'
import { useFirm } from '~/components/hooks/useFirm'
import { apolloClient } from '~/data/apollo'
import { isProd } from '~/util/env'
import { FIRM_SIDS_TO_EXCLUDE_FROM_SESSION_LOGGING } from '~/util/logging'

/**
 * Long tasks at or above this duration are buffered and reported in aggregate. 50ms is the
 * browser's own "long task" threshold; we use a higher bar to keep event volume low.
 */
const LONG_TASK_REPORT_THRESHOLD_MS = 500
/** Long tasks at or above this duration are reported immediately as a MainThreadFreeze event. */
const FREEZE_REPORT_THRESHOLD_MS = 2000
const LONG_TASK_FLUSH_INTERVAL_MS = 30_000
const HEAP_SAMPLE_INTERVAL_MS = 5 * 60_000
/** How far back to look for GraphQL responses that may have triggered a freeze. */
const RECENT_GRAPHQL_LOOKBACK_MS = 15_000
const RECENT_GRAPHQL_BUFFER_SIZE = 20
/**
 * A "saturation" is many medium-length tasks that never individually block for 2s (so
 * MainThreadFreeze doesn't fire) but collectively starve paint/input: the UI appears frozen even
 * though timers still fire and network requests continue to flow. Reported when long tasks consume
 * at least SATURATION_BLOCKED_THRESHOLD_MS of any rolling SATURATION_WINDOW_MS window.
 */
const SATURATION_WINDOW_MS = 10_000
const SATURATION_BLOCKED_THRESHOLD_MS = 6_000
const SATURATION_REPORT_COOLDOWN_MS = 60_000

/**
 * How many cache entries to JSON.stringify when estimating the Apollo cache's size. Serializing
 * the whole cache could block the main thread for seconds in long-lived sessions, so we
 * extrapolate from a sample instead.
 */
const APOLLO_CACHE_SIZE_SAMPLE_COUNT = 200

/**
 * Estimates the Apollo normalized cache footprint without serializing the whole cache:
 * counts entities (cheap) and extrapolates total size from a sample of entries.
 */
function getApolloCacheStats() {
  try {
    const entities = apolloClient.cache.extract() as Record<string, unknown>
    const keys = Object.keys(entities)
    const step = Math.max(1, Math.floor(keys.length / APOLLO_CACHE_SIZE_SAMPLE_COUNT))
    let sampledBytes = 0
    let sampledCount = 0
    for (let i = 0; i < keys.length; i += step) {
      sampledBytes += JSON.stringify(entities[keys[i]!])?.length ?? 0
      sampledCount++
    }
    const estimatedTotalBytes = sampledCount > 0 ? (sampledBytes / sampledCount) * keys.length : 0
    return {
      apolloEntityCount: keys.length,
      apolloEstimatedMb: Math.round((estimatedTotalBytes / 1024 / 1024) * 10) / 10,
    }
  } catch {
    return { apolloEntityCount: null, apolloEstimatedMb: null }
  }
}

interface RecentGraphqlResponse {
  opName: string
  /** performance-timeline timestamp of when the response finished arriving */
  responseEndMs: number
  durationMs: number
  decodedBytes: number
}

/**
 * Reports main-thread health to LogRocket so that customer reports of frozen/unresponsive tabs
 * can be diagnosed after the fact:
 * - MainThreadFreeze: a single task blocked the main thread for 2s+ (the tab appeared frozen)
 * - LongMainThreadTasks: 30s aggregate of tasks that blocked the main thread for 500ms-2s
 * - HeapSample: JS heap usage every 5 minutes (detects degradation in long-lived tabs)
 */
function useMainThreadHealthTracking(firmSid: string | undefined) {
  useEffect(() => {
    if (
      !isProd ||
      !firmSid ||
      FIRM_SIDS_TO_EXCLUDE_FROM_SESSION_LOGGING.includes(firmSid) ||
      typeof PerformanceObserver === 'undefined'
    ) {
      return
    }

    const toMb = (bytes: number | undefined) =>
      isNotNullOrUndefined(bytes) ? Math.round(bytes / 1024 / 1024) : null

    const getPageContext = () => ({
      path: window.location.pathname + window.location.search,
      isVisible: document.visibilityState === 'visible',
      // Heap usage at report time distinguishes GC-pressure pauses (high heap) from
      // data-processing tasks. performance.memory is non-standard (Chrome only).
      usedHeapMb: toMb((performance as { memory?: Record<string, number> }).memory?.usedJSHeapSize),
    })

    // Ring buffer of recent GraphQL responses so that a freeze can be correlated with the
    // response whose processing (parse + cache normalization + re-render) likely caused it.
    const recentGraphqlResponses: RecentGraphqlResponse[] = []
    const graphqlObserver = new PerformanceObserver(list => {
      for (const entry of list.getEntries()) {
        const resource = entry as PerformanceResourceTiming
        if (!resource.name.includes('/graphql')) {
          continue
        }
        let opName = 'unknown'
        try {
          opName = new URL(resource.name).searchParams.get('opName') ?? 'unknown'
        } catch {
          // ignore unparseable URLs
        }
        recentGraphqlResponses.push({
          opName,
          responseEndMs: resource.responseEnd,
          durationMs: Math.round(resource.duration),
          decodedBytes: resource.decodedBodySize,
        })
        if (recentGraphqlResponses.length > RECENT_GRAPHQL_BUFFER_SIZE) {
          recentGraphqlResponses.shift()
        }
      }
    })

    const getGraphqlResponsesPrecedingTask = (task: PerformanceEntry) =>
      recentGraphqlResponses
        .filter(response => response.responseEndMs >= task.startTime - RECENT_GRAPHQL_LOOKBACK_MS)
        .map(response => ({
          opName: response.opName,
          durationMs: response.durationMs,
          decodedKb: Math.round(response.decodedBytes / 1024),
          endedMsBeforeTaskStart: Math.round(task.startTime - response.responseEndMs),
        }))

    let pendingLongTaskDurationsMs: number[] = []
    // Sliding window of all long tasks (the browser reports tasks of 50ms+), used to detect
    // main-thread saturation. See SATURATION_WINDOW_MS above.
    let recentLongTasks: { endTimeMs: number; durationMs: number }[] = []
    let lastSaturationReportTimeMs = 0
    const observer = new PerformanceObserver(list => {
      for (const entry of list.getEntries()) {
        const durationMs = Math.round(entry.duration)

        const taskEndTimeMs = entry.startTime + entry.duration
        recentLongTasks = recentLongTasks.filter(
          task => task.endTimeMs >= taskEndTimeMs - SATURATION_WINDOW_MS
        )
        recentLongTasks.push({ endTimeMs: taskEndTimeMs, durationMs })
        const blockedMsInWindow = recentLongTasks.reduce((sum, task) => sum + task.durationMs, 0)
        if (
          blockedMsInWindow >= SATURATION_BLOCKED_THRESHOLD_MS &&
          taskEndTimeMs - lastSaturationReportTimeMs >= SATURATION_REPORT_COOLDOWN_MS
        ) {
          lastSaturationReportTimeMs = taskEndTimeMs
          logRocketTrackEvent(
            { name: 'MainThreadSaturation', firmSid },
            {
              blockedMsInWindow,
              windowMs: SATURATION_WINDOW_MS,
              taskCount: recentLongTasks.length,
              maxTaskMs: Math.max(...recentLongTasks.map(task => task.durationMs)),
              recentGraphqlResponses: getGraphqlResponsesPrecedingTask(entry),
              ...getPageContext(),
            }
          )
        }

        if (durationMs >= FREEZE_REPORT_THRESHOLD_MS) {
          logRocketTrackEvent(
            { name: 'MainThreadFreeze', firmSid },
            {
              durationMs,
              recentGraphqlResponses: getGraphqlResponsesPrecedingTask(entry),
              ...getPageContext(),
            }
          )
        } else if (durationMs >= LONG_TASK_REPORT_THRESHOLD_MS) {
          pendingLongTaskDurationsMs.push(durationMs)
        }
      }
    })
    try {
      observer.observe({ entryTypes: ['longtask'] })
      graphqlObserver.observe({ entryTypes: ['resource'] })
    } catch {
      // longtask observation is not supported in all browsers (e.g. Safari); skip silently
      observer.disconnect()
      graphqlObserver.disconnect()
      return
    }

    const flushLongTasksInterval = setInterval(() => {
      if (pendingLongTaskDurationsMs.length === 0) {
        return
      }
      logRocketTrackEvent(
        { name: 'LongMainThreadTasks', firmSid },
        {
          count: pendingLongTaskDurationsMs.length,
          maxMs: Math.max(...pendingLongTaskDurationsMs),
          totalMs: pendingLongTaskDurationsMs.reduce((sum, ms) => sum + ms, 0),
          ...getPageContext(),
        }
      )
      pendingLongTaskDurationsMs = []
    }, LONG_TASK_FLUSH_INTERVAL_MS)

    const heapSampleInterval = setInterval(() => {
      const reportHeapSample = () => {
        // performance.memory is non-standard (Chrome only)
        const memory = (performance as { memory?: Record<string, number> }).memory
        if (!memory) {
          return
        }
        logRocketTrackEvent(
          { name: 'HeapSample', firmSid },
          {
            usedMb: toMb(memory.usedJSHeapSize),
            totalMb: toMb(memory.totalJSHeapSize),
            limitMb: toMb(memory.jsHeapSizeLimit),
            // Logging for https://linear.app/rentana/issue/RE-2792/apollo-cache-eviction-for-properties-not-viewed-recently:
            // Correlates heap growth with Apollo cache growth to size a potential cache-eviction
            // effort: if heap grows while the cache stays flat, eviction won't help.
            ...getApolloCacheStats(),
            ...getPageContext(),
          }
        )
      }
      // getApolloCacheStats does O(entityCount) work in cache.extract(); defer to an idle period
      // so sampling never stacks on top of an already-busy frame (the timeout ensures the sample
      // still fires on a saturated main thread, which is exactly when we need the data).
      if (typeof requestIdleCallback === 'function') {
        requestIdleCallback(reportHeapSample, { timeout: 30_000 })
      } else {
        reportHeapSample()
      }
    }, HEAP_SAMPLE_INTERVAL_MS)

    return () => {
      observer.disconnect()
      graphqlObserver.disconnect()
      clearInterval(flushLongTasksInterval)
      clearInterval(heapSampleInterval)
    }
  }, [firmSid])
}

export function LogRocketWrapper({ children }: PropsWithChildren) {
  const user = useCurrentUser()
  const firm = useFirm()
  const { isImpersonating, impersonatedUser, adminUser } = useAdminContext()
  const effectiveUser = isImpersonating ? impersonatedUser : user
  const effectiveUserFullName = isImpersonating
    ? `${impersonatedUser?.fullName} (${adminUser?.fullName})`
    : user.fullName
  const effectiveUserEmail = effectiveUser.email
  const effectiveUserRole = effectiveUser.role
  const effectiveUserSid = effectiveUser.sid

  // Depend on scalars rather than the firm object: the object identity can change on every
  // FetchFirm cache write, which re-ran this effect and spammed LogRocket.init/identify
  // ("More than 10 identify calls on this page" warnings).
  const firmSid = firm?.sid
  const firmName = firm?.name

  useMainThreadHealthTracking(firmSid)

  useEffect(
    function initLogRocket() {
      if (isProd && firmSid && !FIRM_SIDS_TO_EXCLUDE_FROM_SESSION_LOGGING.includes(firmSid)) {
        const SENSITIVE_HEADERS = [
          'Authorization',
          'Cookie',
          'Set-Cookie',
          'X-CSRF-Token',
          'X-XSRF-Token',
          'Session',
          'X-API-Key',
          'Bearer',
          'access_token',
          'refresh_token',
          'id_token',
          'token',
          'sessionid',
          'x-auth-token',
        ]

        const { requestSanitizer, responseSanitizer } =
          LogRocketFuzzySanitizer.setup(SENSITIVE_HEADERS)

        LogRocket.init('lhdfyv/prod', {
          network: {
            requestSanitizer: request => {
              const requestWithBody = { ...request, body: request.body ?? null }
              return requestSanitizer(requestWithBody)
            },
            responseSanitizer: response => {
              const responseWithBody = { ...response, body: response.body ?? null }
              return responseSanitizer(responseWithBody)
            },
          },
        })
        LogRocket.identify(effectiveUserSid, {
          name: effectiveUserFullName,
          email: effectiveUserEmail,
          role: effectiveUserRole,
          firmSid,
          firmName: firmName ?? '',
          isImpersonating,
        })
        LogRocket.getSessionURL(sessionURL => {
          Sentry.setExtra('sessionURL', sessionURL)
        })
      }
    },
    [
      firmSid,
      firmName,
      effectiveUserEmail,
      effectiveUserFullName,
      effectiveUserRole,
      effectiveUserSid,
      isImpersonating,
    ]
  )
  return <>{children}</>
}

export function logRocketTrackEvent(
  { name, firmSid }: { name: string; firmSid: string },
  data: unknown
) {
  try {
    if (isProd && firmSid && !FIRM_SIDS_TO_EXCLUDE_FROM_SESSION_LOGGING.includes(firmSid)) {
      LogRocket.track(name, { data: JSON.stringify(data) })
    }
  } catch (error) {
    console.error(error)
  }
}
